import { css } from '@emotion/core'
import { graphql } from 'gatsby'
import React from 'react'

import Body from '../components/Body'
import { CardsNavigation, stickyCardsNavigationHeight } from '../components/CardsNavigation'
import { categoryLinkStyle } from '../components/content-management/blog/BlogPostCard'
import GlossaryAlphabetBlock, {
  alphabeticalRegisterHeight,
} from '../components/content-management/GlossaryAlphabeticalBlock'
import CTAMain from '../components/ctas/CTAMain'
import Divider from '../components/Divider'
import Footer from '../components/Footer'
import Grid from '../components/Grid'
import { IntroHeroBlock } from '../components/IntroHeroBlock'
import Layout from '../components/Layout'
import { Link } from '../components/Link'
import ScrollToTop from '../components/ScrollToTop'
import { searchBarHeight, SearchResultsBlock } from '../components/Search'
import SEO from '../components/SEO'
import TextCard from '../components/TextCard'
import { breakpoints } from '../constants/breakPoints'
import { glossaryAlphabetBreakPoint, glossaryBreakPoint, headerBreakPoint } from '../constants/breakPoints'
import { HEADER_HEIGHT } from '../constants/header'
import { reloadIfCurrentPathMatchesRedirect } from '../helpers/common'
import { getCtaMainType } from '../helpers/cta'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { GlossaryPageProps, GlossaryTermProps, RegisterEntryProps } from '../interfaces/glossary'
import { GlossaryHeader } from '../pageTemplates/glossaryTerm'

const defaultCopy =
  'Check out our glossary of common agile, feedback management, prioritization, <br class="hide-on-small" /> product management, product strategy, and roadmapping terms and definitions.'

const GlossaryPage: React.FunctionComponent<GlossaryPageProps> = ({ data, pageContext }) => {
  const { name, slug, glossaryPageCopy } = pageContext

  const [search, setSearch] = React.useState('')
  const debouncedSearch = useDebouncedValue(search)

  const register = React.useMemo(() => {
    const alphabet = new Array(26).fill(0).map((_, i) => String.fromCharCode(i + 65))

    const alphabeticalTerms: { character: string; terms: GlossaryTermProps[] }[] = alphabet.map((char) => ({
      character: char,
      terms: [],
    }))

    const specialCharacterTerms: { character: string; terms: GlossaryTermProps[] } = {
      character: '#',
      terms: [],
    }

    data.glossaryTerms.edges.forEach((edge) => {
      const firstChar = edge.node.title.trim().substr(0, 1).toUpperCase()
      const index = alphabet.indexOf(firstChar)

      if (index !== -1) {
        alphabeticalTerms[index].terms.push(edge.node)
      } else {
        specialCharacterTerms.terms.push(edge.node)
      }
    })

    return [specialCharacterTerms, ...alphabeticalTerms]
  }, [data])

  const alphabeticalRegister = React.useMemo(() => register.filter((entry) => entry.terms.length > 0), [register])

  // filter register by selected glossary category - if slug = "":
  // => no category selected  => main glossary page  => show all terms (alphabetical register)
  const filteredCategoryRegister = slug
    ? React.useMemo<RegisterEntryProps[]>(
        () =>
          alphabeticalRegister
            .map((registerEntry) => ({
              ...registerEntry,
              terms: registerEntry.terms.filter(({ category }) => category[0].slug === slug),
            }))
            .filter((registerEntry) => registerEntry.terms.length > 0),
        [alphabeticalRegister, slug]
      )
    : alphabeticalRegister

  // filter searchTerms by (debounced) search term
  const filteredSearchTerms = React.useMemo(
    () =>
      data.glossaryTerms.edges.filter(({ node: { title } }) =>
        title.toLowerCase().includes(debouncedSearch.toLowerCase())
      ),
    [debouncedSearch]
  )

  return (
    <Layout>
      <SEO
        title={`Glossary ${name ? 'category: ' + name : ''}`}
        description={
          glossaryPageCopy ||
          "Unlock the essentials of product management with our comprehensive glossary. Discover key terms, definitions, and insights to enhance your skills and boost your team's success. Start mastering product management today."
        }
      />
      <GlossaryHeader search={search} setSearch={setSearch} />
      <Body>
        <ScrollToTop referenceIdForVisibility={'glossary-categories'} />

        <IntroHeroBlock
          upperText={
            name ? (
              <h2 className={'heading4 center'}>
                <Link className={'opacity-on-hover'} href={'/glossary/'}>
                  Glossary
                </Link>
              </h2>
            ) : null
          }
          headlineText={name ? name : 'Glossary'}
          subheaderText={glossaryPageCopy ? glossaryPageCopy : defaultCopy}
          withoutCTA
          withoutBorder
          withoutMarginBottom
          cssStyles={glossaryIntroStyle(!!name)}
        />

        <div>
          <CardsNavigation
            isCentered
            stickyLayoutOnBreakPoint
            id={'glossary-categories'}
            hrefPrefix={'glossary/category'}
            categories={data.allContentfulGlossaryTermCategory.edges}
            cssStyles={cardsNavigationStyle}
          />

          <GlossaryAlphabetBlock search={'search'} register={register} filteredRegister={filteredCategoryRegister} />

          <div css={termsGridStyle}>
            {React.useMemo(() => {
              return filteredCategoryRegister.map((entry, index) => (
                <React.Fragment key={entry.character}>
                  <h2 css={glossaryHeadlineStyle}>
                    <div id={entry.character.toLowerCase()} css={glossaryHeadlineAnchorStyle(search)} />
                    {entry.character}
                  </h2>
                  <Grid columns={3} alignContent={'flex-start'} breakPoint={glossaryBreakPoint} twoColumnsForTablet>
                    {entry.terms.map((term) => (
                      <TextCard
                        key={term.slug}
                        title={term.title}
                        description={term.seoAbstract}
                        linkText={'Read more'}
                        linkUrl={`/glossary/${term.slug}/`}
                        cssStyles={glossaryTextCardStyle}
                      />
                    ))}
                  </Grid>
                  {index !== filteredCategoryRegister.length - 1 && <Divider size={4} />}
                </React.Fragment>
              ))
            }, [alphabeticalRegister])}
          </div>
        </div>

        <CTAMain type={getCtaMainType(slug || '')} />

        {/* slide-up for search results (overlay) */}
        <SearchResultsBlock
          search={search}
          debouncedSearch={debouncedSearch}
          resultsArray={filteredSearchTerms}
          resultsBlockContent={filteredSearchTerms.map(({ node: filteredTerm }) => (
            <TextCard
              additionalJsx={
                <Link
                  className={'strong-body-2 opacity-on-hover'}
                  href={`/glossary/category/${filteredTerm.category[0].slug}/`}
                  onClick={(event) => {
                    event.stopPropagation()
                    reloadIfCurrentPathMatchesRedirect(event, `/glossary/category/${filteredTerm.category[0].slug}/`)
                  }}
                  css={categoryLinkStyle(filteredTerm.category[0].categoryColor)}
                >
                  {filteredTerm.category[0].name}
                </Link>
              }
              key={filteredTerm.slug}
              title={filteredTerm.title}
              description={filteredTerm.seoAbstract}
              linkText={'Read more'}
              linkUrl={`/glossary/${filteredTerm.slug}/`}
              cssStyles={glossaryTextCardStyle}
            />
          ))}
          breakPoint={glossaryBreakPoint}
        />
      </Body>
      <Footer />
    </Layout>
  )
}

export default GlossaryPage

export const query = graphql`
  query GlossaryPage {
    glossaryTerms: allContentfulGlossaryTerm(sort: { title: ASC }) {
      edges {
        node {
          id
          slug
          title
          abstract {
            abstract
          }
          seoAbstract
          category {
            slug
            name
            categoryColor
          }
        }
      }
    }

    allContentfulGlossaryTermCategory(sort: { name: ASC }) {
      edges {
        node {
          id
          slug
          name
          categoryColor
          navigationIcon {
            gatsbyImageData(width: 60, quality: 90)
          }
        }
      }
    }
  }
`

const glossaryIntroStyle = (isCategoryPage: boolean) => css`
  .heading1 {
    // if isCategoryPage: link to glossary main LP should be clickable easily
    // if not: no link above title (Glossary) => push down h1 via padding-top
    ${isCategoryPage ? 'pointer-events: none;' : 'padding-top: 26px !important;'};
  }

  .subtitle {
    max-width: 46rem;
  }

  @media screen and (max-width: ${headerBreakPoint}px) {
    padding-bottom: 1rem;
  }
`

const cardsNavigationStyle = css`
  margin-bottom: 2rem;

  @media screen and (max-width: ${headerBreakPoint}px) {
    margin: 4rem 0;
  }
`

const glossaryHeadlineStyle = css`
  color: #dfe0e3;
  font-weight: 300 !important;
  font-size: 9rem;
  line-height: 7.5rem;
  position: relative;
  margin-bottom: 2rem;
`

const glossaryHeadlineAnchorStyle = (search: string) => css`
  position: absolute;
  left: 0;
  top: -${Math.max(HEADER_HEIGHT + alphabeticalRegisterHeight + 32 - 1, 0)}px;

  // <GlossaryAlphabetBlock /> not sticky any more
  @media screen and (max-width: ${glossaryAlphabetBreakPoint}px) {
    top: -${Math.max(HEADER_HEIGHT + 32 - 1, 0)}px;
  }

  // there will either be the CardsNavigation (not searching) or the SearchBarComponent (searching)
  @media screen and (max-width: ${breakpoints.small}px) {
    top: -${Math.max(HEADER_HEIGHT + (search ? searchBarHeight : stickyCardsNavigationHeight) + 32 - 1, 0)}px;
  }
`

const termsGridStyle = css`
  margin-top: 4rem;
`

// export for glossaryTerm.tsx
export const glossaryTextCardStyle = css`
  #text-card-content {
    border-radius: 0.5rem;
    border-top: 1px solid #efeff0;

    .heading4 {
      margin-top: 0.5rem;
    }
  }
`
